import { useState, useRef, useEffect, useCallback } from 'react';
import type { ReactElement } from 'react';
import type { TRPCClientError } from '@trpc/client';
import { trpc } from '../trpc';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card } from './ui/card';
import { Spinner } from './ui/spinner';
import { toast } from 'sonner';

// Icons (you may need to install lucide-react if not already available)
const MessageCircleIcon = () => (
  <svg
    className='w-6 h-6'
    fill='none'
    stroke='currentColor'
    viewBox='0 0 24 24'
  >
    <path
      strokeLinecap='round'
      strokeLinejoin='round'
      strokeWidth={2}
      d='M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z'
    />
  </svg>
);

const XIcon = () => (
  <svg
    className='w-4 h-4'
    fill='none'
    stroke='currentColor'
    viewBox='0 0 24 24'
  >
    <path
      strokeLinecap='round'
      strokeLinejoin='round'
      strokeWidth={2}
      d='M6 18L18 6M6 6l12 12'
    />
  </svg>
);

const SendIcon = () => (
  <svg
    className='w-4 h-4'
    fill='none'
    stroke='currentColor'
    viewBox='0 0 24 24'
  >
    <path
      strokeLinecap='round'
      strokeLinejoin='round'
      strokeWidth={2}
      d='M12 19l9 2-9-18-9 18 9-2zm0 0v-8'
    />
  </svg>
);



interface Message {
  role: 'user' | 'assistant';
  content: string;
  sources?: { title: string; url?: string }[];
  timestamp: Date;
}

// Simple markdown renderer for basic formatting
function renderMarkdown(text: string): ReactElement {
  // Split text by double newlines to create paragraphs
  const paragraphs = text.split('\n\n');

  // Allow only safe URL protocols
  const isSafeUrl = (url: string) => {
    try {
      const u = url.trim().toLowerCase();
      // Allow same-origin relative paths and common safe protocols
      if (u.startsWith('/') || u.startsWith('./') || u.startsWith('../'))
        return true;
      return (
        u.startsWith('http:') ||
        u.startsWith('https:') ||
        u.startsWith('mailto:') ||
        u.startsWith('tel:')
      );
    } catch {
      return false;
    }
  };

  const renderInlineMarkdown = (str: string): (string | ReactElement)[] => {
    // Process the string for inline markdown
    const processString = (
      input: string,
      startIndex: number = 0
    ): (string | ReactElement)[] => {
      const result: (string | ReactElement)[] = [];
      let i = startIndex;
      let textBuffer = '';

      while (i < input.length) {
        const char = input[i];

        // Check for bold (**text**)
        if (char === '*' && input[i + 1] === '*' && i + 2 < input.length) {
          // Find the closing **
          const endIndex = input.indexOf('**', i + 2);
          if (endIndex !== -1) {
            // Add any text before the bold
            if (textBuffer) {
              result.push(textBuffer);
              textBuffer = '';
            }

            const boldText = input.substring(i + 2, endIndex);
            result.push(
              <strong key={`bold-${i}`} className='font-semibold'>
                {boldText}
              </strong>
            );
            i = endIndex + 2;
            continue;
          }
        }

        // Check for italic (*text*)
        if (char === '*' && input[i + 1] !== '*' && i + 1 < input.length) {
          // Find the closing *
          const endIndex = input.indexOf('*', i + 1);
          if (endIndex !== -1) {
            // Add any text before the italic
            if (textBuffer) {
              result.push(textBuffer);
              textBuffer = '';
            }

            const italicText = input.substring(i + 1, endIndex);
            result.push(
              <em key={`italic-${i}`} className='italic'>
                {italicText}
              </em>
            );
            i = endIndex + 1;
            continue;
          }
        }

        // Check for headers (### text)
        if (char === '#' && (i === 0 || input[i - 1] === '\n')) {
          let headerLevel = 1;
          let headerStart = i + 1;

          // Count consecutive # characters (up to 6)
          while (
            headerStart < input.length &&
            input[headerStart] === '#' &&
            headerLevel < 6
          ) {
            headerLevel++;
            headerStart++;
          }

          // Look for the end of the line
          const lineEnd = input.indexOf('\n', headerStart);
          const headerEnd = lineEnd === -1 ? input.length : lineEnd;

          if (headerStart < headerEnd) {
            // Add any text before the header
            if (textBuffer) {
              result.push(textBuffer);
              textBuffer = '';
            }

            const headerText = input.substring(headerStart, headerEnd).trim();
            const headerClass =
              headerLevel === 1
                ? 'text-lg font-bold'
                : headerLevel === 2
                  ? 'text-base font-semibold'
                  : 'text-sm font-semibold';

            result.push(
              <div key={`header-${i}`} className={`${headerClass} mt-2 mb-1`}>
                {headerText}
              </div>
            );

            i = headerEnd;
            continue;
          }
        }

        // Check for links [text](url)
        if (char === '[') {
          const textEndIndex = input.indexOf(']', i + 1);
          if (textEndIndex !== -1 && input[textEndIndex + 1] === '(') {
            const urlEndIndex = input.indexOf(')', textEndIndex + 2);
            if (urlEndIndex !== -1) {
              // Add any text before the link
              if (textBuffer) {
                result.push(textBuffer);
                textBuffer = '';
              }

              const linkText = input.substring(i + 1, textEndIndex);
              const linkUrl = input.substring(textEndIndex + 2, urlEndIndex);

              if (isSafeUrl(linkUrl)) {
                result.push(
                  <a
                    key={`link-${i}`}
                    href={linkUrl}
                    target='_blank'
                    rel='noopener noreferrer'
                    className='text-blue-600 hover:text-blue-800 underline'
                  >
                    {linkText}
                  </a>
                );
              } else {
                // Fallback to plain text if URL is not safe
                result.push(linkText);
              }
              i = urlEndIndex + 1;
              continue;
            }
          }
        }

        textBuffer += char;
        i++;
      }

      // Add any remaining text
      if (textBuffer) {
        result.push(textBuffer);
      }

      return result;
    };

    return processString(str);
  };

  return (
    <div className='space-y-2'>
      {paragraphs.map((paragraph, pIndex) => {
        if (!paragraph.trim()) return null;

        // Check if it's a list item (starts with - or number.)
        const lines = paragraph.split('\n');
        const isListItem = lines.some(
          (line) => /^\s*[-*]\s/.test(line) || /^\s*\d+\.\s/.test(line)
        );

        if (isListItem) {
          return (
            <ul key={pIndex} className='list-disc list-inside space-y-1 ml-2'>
              {lines.map((line, lIndex) => {
                const trimmedLine = line.trim();
                if (!trimmedLine) return null;

                // Remove list markers
                const cleanLine = trimmedLine
                  .replace(/^[-*]\s/, '')
                  .replace(/^\d+\.\s/, '');

                return (
                  <li key={lIndex} className='text-sm'>
                    {renderInlineMarkdown(cleanLine)}
                  </li>
                );
              })}
            </ul>
          );
        } else {
          return (
            <p key={pIndex} className='text-sm'>
              {renderInlineMarkdown(paragraph)}
            </p>
          );
        }
      })}
    </div>
  );
}


interface MessageBubbleProps {
  message: Message;
}

function MessageBubble({ message }: MessageBubbleProps) {
  return (
    <div
      className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
    >
      <div
        className={`max-w-[80%] rounded-lg p-3 ${
          message.role === 'user'
            ? 'bg-blue-600 text-white'
            : 'bg-gray-100 text-gray-900'
        }`}
      >
        <div className='leading-relaxed'>
          {message.role === 'assistant' ? (
            renderMarkdown(message.content)
          ) : (
            <div className='whitespace-pre-wrap text-sm'>{message.content}</div>
          )}
        </div>
      </div>
    </div>
  );
}

// Countdown timer component for rate limiting
function CountdownTimer({ resetTime, onComplete }: { resetTime: Date; onComplete: () => void }) {
  const [timeLeft, setTimeLeft] = useState('');

  useEffect(() => {
    const updateTimer = () => {
      const now = new Date().getTime();
      const resetTimeMs = resetTime.getTime();
      const difference = resetTimeMs - now;

      if (difference <= 0) {
        setTimeLeft('');
        onComplete();
        return;
      }

      const minutes = Math.floor(difference / (1000 * 60));
      const seconds = Math.floor((difference % (1000 * 60)) / 1000);
      
      if (minutes > 0) {
        setTimeLeft(`${minutes}m ${seconds}s`);
      } else {
        setTimeLeft(`${seconds}s`);
      }
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);

    return () => clearInterval(interval);
  }, [resetTime, onComplete]);

  return timeLeft ? (
    <span className='text-xs text-orange-600 font-mono'>
      Next message in: {timeLeft}
    </span>
  ) : null;
}

export function ChatWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [sessionId, setSessionId] = useState<string>();
  const [isRateLimited, setIsRateLimited] = useState(false);
  const [rateLimitResetTime, setRateLimitResetTime] = useState<Date | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Callback to clear rate limit state
  const clearRateLimit = useCallback(() => {
    setIsRateLimited(false);
    setRateLimitResetTime(null);
  }, []);

  // tRPC mutations and queries
  const sendMessageMutation = trpc.chat.sendMessage.useMutation({
    onSuccess: (data: { message: string; sessionId: string; sources: { title: string; url?: string }[] }) => {
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: data.message,
          sources: data.sources,
          timestamp: new Date(),
        },
      ]);
      setSessionId(data.sessionId);

      // Focus input after response
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    },
    onError: (error: TRPCClientError<any>) => {
      console.error('Chat error:', error);

      // Handle rate limiting specifically
      if (error.data?.code === 'TOO_MANY_REQUESTS') {
        setIsRateLimited(true);
        
        // Extract reset time from error message if available
        const resetTimeMatch = error.message?.match(/Try again at ([^.]+)\./);  
        if (resetTimeMatch) {
          try {
            const resetTime = new Date(resetTimeMatch[1]);
            setRateLimitResetTime(resetTime);
            
            // Clear rate limit state after the reset time
            const timeUntilReset = resetTime.getTime() - new Date().getTime();
            if (timeUntilReset > 0) {
              setTimeout(clearRateLimit, timeUntilReset);
            }
          } catch (e) {
            console.warn('Could not parse rate limit reset time:', resetTimeMatch[1]);
          }
        } else {
          // Fallback: clear rate limit after 60 seconds
          setTimeout(clearRateLimit, 60_000);
        }
        
        toast.error("Rate limit reached - please wait before sending another message");
        setMessages((prev) => [
          ...prev,
          {
            role: 'assistant',
            content:
              "### 🚦 Message Rate Limit Reached\n\n" +
              "You've reached our **30 messages per minute** limit to ensure optimal performance for all users.\n\n" +
              (error.message?.includes('Try again at') 
                ? error.message.replace('Rate limit exceeded. You can send up to 30 messages per minute. ', '') + "\n\n"
                : "Please wait a moment before sending your next message.\n\n") +
              "💡 **This is a system feature, not an error.** You can continue chatting once the rate limit resets!",
            timestamp: new Date(),
          },
        ]);
      } else {
        toast.error('Sorry, I encountered an error. Please try again.');
        setMessages((prev) => [
          ...prev,
          {
            role: 'assistant',
            content: 'Sorry, I encountered an error. Please try again.',
            timestamp: new Date(),
          },
        ]);
      }
    },
  });

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(scrollToBottom, [messages]);

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [isOpen]);

  const handleSend = () => {
    if (!input.trim() || sendMessageMutation.isLoading) return;

    const userMessage: Message = {
      role: 'user',
      content: input.trim(),
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);

    sendMessageMutation.mutate({
      message: input.trim(),
      sessionId,
      platform: 'web',
    });

    setInput('');
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const closeChat = () => {
    setIsOpen(false);
  };

  // Floating chat button
  if (!isOpen) {
    return (
      <div className='fixed bottom-6 right-6 z-50'>
        <Button
          onClick={() => setIsOpen(true)}
          className='rounded-full w-14 h-14 shadow-lg bg-blue-600 hover:bg-blue-700 transition-all duration-200 hover:scale-105'
          size='icon'
          aria-label='Open help chat'
        >
          <MessageCircleIcon />
        </Button>
      </div>
    );
  }

  // Chat modal
  return (
    <div
      className='fixed bottom-6 right-6 z-50 w-96 h-[500px]'
      role='dialog'
      aria-modal='true'
      aria-labelledby='chat-assistant-title'
    >
      <Card className='flex flex-col h-full shadow-2xl border-0 ring-1 ring-black/10 py-0'>
        {/* Header */}
        <div className='flex items-center justify-between p-4 border-b bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-t-lg'>
          <div>
            <h3 id='chat-assistant-title' className='font-semibold text-sm'>
              Help Assistant
            </h3>
            <p className='text-xs text-blue-100'>Ask me about Trackplicant</p>
          </div>
          <Button
            variant='ghost'
            size='icon'
            onClick={closeChat}
            className='text-white hover:bg-white/20 h-8 w-8'
            aria-label='Close chat'
          >
            <XIcon />
          </Button>
        </div>

        {/* Messages */}
        <div
          className='flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50/50'
          role='log'
          aria-live='polite'
          aria-relevant='additions text'
        >
          {messages.length === 0 && (
            <div className='text-center py-8'>
              <div className='w-12 h-12 mx-auto mb-4 bg-blue-100 rounded-full flex items-center justify-center'>
                <MessageCircleIcon />
              </div>
              <h4 className='font-medium text-gray-900 mb-2'>
                Hi! I'm here to help
              </h4>
              <p className='text-sm text-gray-600 mb-4'>
                Ask me about Trackplicant features, getting started, or anything
                else you need help with.
              </p>

              {/* Quick action buttons */}
              <div className='space-y-2'>
                <Button
                  variant='outline'
                  size='sm'
                  onClick={() => {
                    setMessages((prev) => [
                      ...prev,
                      {
                        role: 'user',
                        content: 'Ok, I signed up, what now?',
                        timestamp: new Date(),
                      },
                    ]);
                    sendMessageMutation.mutate({
                      message: 'Ok, I signed up, what now?',
                      sessionId,
                      platform: 'web',
                    });
                  }}
                  className='w-full text-left justify-start text-xs'
                  disabled={sendMessageMutation.isLoading}
                >
                  "I just signed up, what now?"
                </Button>
                <Button
                  variant='outline'
                  size='sm'
                  onClick={() => {
                    setMessages((prev) => [
                      ...prev,
                      {
                        role: 'user',
                        content: 'How do I add my first application?',
                        timestamp: new Date(),
                      },
                    ]);
                    sendMessageMutation.mutate({
                      message: 'How do I add my first application?',
                      sessionId,
                      platform: 'web',
                    });
                  }}
                  className='w-full text-left justify-start text-xs'
                  disabled={sendMessageMutation.isLoading}
                >
                  "How do I add my first application?"
                </Button>
                <Button
                  variant='outline'
                  size='sm'
                  onClick={() => {
                    setMessages((prev) => [
                      ...prev,
                      {
                        role: 'user',
                        content: 'What are the different plans?',
                        timestamp: new Date(),
                      },
                    ]);
                    sendMessageMutation.mutate({
                      message: 'What are the different plans?',
                      sessionId,
                      platform: 'web',
                    });
                  }}
                  className='w-full text-left justify-start text-xs'
                  disabled={sendMessageMutation.isLoading}
                >
                  "What are the different plans?"
                </Button>
              </div>
            </div>
          )}

          {messages.map((message, idx) => (
            <MessageBubble
              key={`${message.timestamp instanceof Date ? message.timestamp.getTime() : String(message.timestamp)}-${message.role}-${idx}`}
              message={message}
            />
          ))}

          {/* Loading indicator */}
          {sendMessageMutation.isLoading && (
            <div className='flex justify-start'>
              <div className='bg-gray-100 rounded-lg p-3 flex items-center gap-2'>
                <Spinner size='sm' />
                <span className='text-sm text-gray-600'>Thinking...</span>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className='border-t p-4 bg-white rounded-b-lg'>
          {/* Rate limit indicator */}
          {isRateLimited && (
            <div className='mb-3 p-3 bg-orange-50 border border-orange-200 rounded-lg'>
              <div className='flex items-center justify-between text-orange-800'>
                <div className='flex items-center gap-2'>
                  <div className='w-2 h-2 bg-orange-400 rounded-full animate-pulse'></div>
                  <span className='text-sm font-medium'>
                    Rate limit active (30 messages/minute)
                  </span>
                </div>
                {rateLimitResetTime && (
                  <CountdownTimer 
                    resetTime={rateLimitResetTime} 
                    onComplete={clearRateLimit}
                  />
                )}
              </div>
              {rateLimitResetTime && (
                <div className='text-xs text-orange-600 mt-1'>
                  You can send messages again at {rateLimitResetTime.toLocaleTimeString()}
                </div>
              )}
            </div>
          )}
          
          <div className='flex gap-2'>
            <Input
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={isRateLimited ? 'Please wait before sending another message...' : 'Ask me anything about Trackplicant...'}
              disabled={sendMessageMutation.isLoading || isRateLimited}
              className={`flex-1 text-sm ${isRateLimited ? 'bg-gray-50 text-gray-500' : ''}`}
              maxLength={1000}
            />
            <Button
              onClick={handleSend}
              disabled={!input.trim() || sendMessageMutation.isLoading || isRateLimited}
              size='icon'
              className='shrink-0'
              aria-label='Send message'
            >
              {sendMessageMutation.isLoading ? (
                <Spinner size='sm' />
              ) : (
                <SendIcon />
              )}
            </Button>
          </div>

          {/* Character count */}
          {input.length > 800 && (
            <div className='mt-1 text-right'>
              <span
                className={`text-xs ${input.length > 950 ? 'text-red-500' : 'text-gray-500'}`}
              >
                {input.length}/1000
              </span>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}
